// iter891 — Printer Diagnostics tile.
//
// A one-glance health card that renders on POS → Devices page. For each
// configured printer it shows: connection type, VID/PID resolved (Y/N),
// last successful print timestamp, DeviceNotFound error count in the
// last 24h, and a "Ping now" button that fires a tiny status probe.
//
// Data source: existing /api/cashier/printers list — augmented with the
// per-printer `last_print_at`, `last_print_error`, `error_count_24h`
// fields that the backend already stamps after every print attempt.
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Printer, CheckCircle2, AlertTriangle, XCircle, Zap, Usb, Wifi, Bluetooth, Radar,
} from 'lucide-react';

function ago(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function connectionIcon(ct) {
  switch (ct) {
    case 'usb': return <Usb size={12} />;
    case 'bluetooth': return <Bluetooth size={12} />;
    case 'sunmi': return <Printer size={12} />;
    case 'airprint': return <Wifi size={12} />;
    default: return <Wifi size={12} />;
  }
}

function statusOf(p) {
  const err = Number(p.error_count_24h || 0);
  if (err >= 3) return { kind: 'critical', label: `${err} errors 24h`, color: 'text-rose-600' };
  if (err > 0) return { kind: 'warn', label: `${err} error${err === 1 ? '' : 's'} 24h`, color: 'text-amber-600' };
  if (!p.last_print_at) return { kind: 'idle', label: 'never printed', color: 'text-muted-foreground' };
  return { kind: 'ok', label: `last print ${ago(p.last_print_at)}`, color: 'text-emerald-600' };
}

export function PrinterDiagnosticsTile() {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/cashier/printers');
      setPrinters(res.data || []);
    } catch {
      // Silent — the tile just won't render if creds are wrong.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    const c = { ok: 0, warn: 0, critical: 0, idle: 0 };
    for (const p of printers) c[statusOf(p).kind] += 1;
    return c;
  }, [printers]);

  const ping = async (p) => {
    setPinging((b) => ({ ...b, [p.id]: true }));
    try {
      const res = await api.post(`/cashier/printers/${p.id}/ping`);
      if (res.data?.ok) toast.success(`${p.name} responded in ${res.data.latency_ms || '?'}ms`);
      else toast.error(res.data?.error || `${p.name} did not respond`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ping failed');
    } finally {
      setPinging((b) => { const nb = { ...b }; delete nb[p.id]; return nb; });
    }
  };

  if (!loading && printers.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm" data-testid="printer-diagnostics-tile">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Radar size={14} className="text-indigo-600" />
          Printer Diagnostics
          <Badge variant="secondary" className="text-[10px]" data-testid="printer-diagnostics-count">
            {printers.length}
          </Badge>
        </CardTitle>
        <div className="flex gap-1 text-[10px]">
          {summary.ok > 0 && <Badge variant="outline" className="border-emerald-300 text-emerald-700"><CheckCircle2 size={10} className="mr-1" /> {summary.ok} healthy</Badge>}
          {summary.warn > 0 && <Badge variant="outline" className="border-amber-300 text-amber-700"><AlertTriangle size={10} className="mr-1" /> {summary.warn} warning</Badge>}
          {summary.critical > 0 && <Badge variant="outline" className="border-rose-300 text-rose-700"><XCircle size={10} className="mr-1" /> {summary.critical} critical</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-2">
            {printers.map((p) => {
              const st = statusOf(p);
              const vidPidOk = !!(p.usb_vendor_id && p.usb_product_id) || p.connection_type !== 'usb';
              return (
                <div
                  key={p.id}
                  className="rounded-lg border border-border p-2.5 bg-background/50 flex items-center gap-3"
                  data-testid={`printer-row-${p.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Printer size={14} className={st.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate" data-testid={`printer-name-${p.id}`}>{p.name}</span>
                      <Badge variant="outline" className="text-[10px] gap-1 capitalize">
                        {connectionIcon(p.connection_type)} {p.connection_type || 'network'}
                      </Badge>
                      {p.connection_type === 'usb' && (
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${vidPidOk ? 'border-emerald-300 text-emerald-700' : 'border-amber-300 text-amber-700'}`}
                          data-testid={`printer-vidpid-${p.id}`}
                        >
                          {vidPidOk ? 'VID/PID ✓' : 'VID/PID missing'}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3">
                      <span className={st.color} data-testid={`printer-status-${p.id}`}>{st.label}</span>
                      {p.epos_device_id && <span className="font-mono">devid: {p.epos_device_id}</span>}
                      {p.last_print_error && (
                        <span className="text-rose-500 truncate max-w-[240px]" title={p.last_print_error}>
                          err: {p.last_print_error.slice(0, 40)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 text-xs shrink-0"
                    disabled={!!pinging[p.id]}
                    onClick={() => ping(p)}
                    data-testid={`printer-ping-${p.id}`}
                  >
                    <Zap size={11} /> Ping
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PrinterDiagnosticsTile;
